import { cva } from 'class-variance-authority';
import { forwardRef, type ComponentPropsWithoutRef, type MouseEvent, type ReactNode } from 'react';
import { isLoadingTextMuchShorter } from '../lib/buttonUtils';
import { useLoadingCallback } from '../lib/useLoadingCallback';
import { cn } from '../lib/utils';
import { Button as ShadcnButton } from './ui/button';
import { Spinner } from './ui/spinner';

type ButtonProps = ComponentPropsWithoutRef<typeof ShadcnButton> & {
  /** An async onClick handler, if provided, will show a loading spinner while the promise is pending */
  onClickAsync?: (e: MouseEvent<HTMLButtonElement>) => Promise<unknown> | undefined;
  isLoading?: boolean;
  /**
   * Optional text shown next to the spinner while loading. The button's width is preserved from its
   * idle children — when the loading text is of similar length, the spinner and text are spaced
   * evenly across that width; when it is much shorter (or omitted), they are centered to avoid an
   * awkward gap; when longer, the text is truncated with an ellipsis.
   */
  loadingText?: ReactNode;
};

// Named distinctly from `ui/button`'s own `buttonVariants` (also re-exported from the package
// root) to avoid a duplicate-export collision — this is the Solink-specific layer on top of it.
//
// Stock `ui/button` (generated — do not edit) paints dark fills with opacity mixes
// (`dark:bg-destructive/60`, `dark:bg-input/30`, `hover:bg-*/90`). Those bypass the Figma
// interaction tokens already bound to `--primary` / `--destructive` / etc. Override here so
// idle + hover resolve to full semantic colours in both themes.
export const solinkButtonVariants = cva(
  'relative aria-invalid:ring-inset aria-invalid:ring-destructive aria-invalid:ring-1 aria-invalid:w-fit',
  {
    variants: {
      variant: {
        // Idle already uses `bg-primary` → interaction-primary. Only hover needs a ramp step.
        default:
          'hover:bg-interaction-primary-background-hover dark:hover:bg-interaction-primary-background-hover',
        // Restore full-strength fill in dark; hover from the destructive ramp (not /90).
        // `dark:hover:` is required so it wins over `dark:bg-*` on specificity/source order.
        destructive:
          'dark:bg-interaction-destructive-background-default hover:bg-interaction-destructive-background-hover dark:hover:bg-interaction-destructive-background-hover',
        // Kill the translucent dark wash; match Figma outline (transparent + muted border).
        // Hover uses the neutral interaction ramp shared with ghost.
        // `hover:text-accent-foreground` in `ui/button` resolves on `:root`, so a `.dark`
        // subtree gets the light text colour on hover. The interaction token resolves here.
        outline:
          'dark:border-border-muted dark:bg-transparent hover:bg-interaction-neutral-background-hover dark:hover:bg-interaction-neutral-background-hover hover:text-interaction-neutral-content-default',
        // Idle already uses secondary tokens. Only hover needs the Gray ramp (not /80 fade).
        secondary: 'hover:bg-interaction-secondary-background-hover',
        // Rest text inherits from `.tw { text-foreground }`. Override shadcn's dark hover wash.
        ghost:
          'dark:hover:bg-interaction-neutral-background-hover hover:text-interaction-neutral-content-default',
        link: '',
      },
      size: {
        default: 'rounded-lg px-3 py-2 h-8 label-md',
        xs: '', //todo: add styles, added to fix lint errors
        sm: 'rounded px-2 py-1 h-7 label-sm',
        lg: 'rounded-lg px-4 py-3 h-10 label-md',
        icon: 'size-9 rounded-lg p-1',
        'icon-xs': '', //todo: add styles, added to fix lint errors
        'icon-sm': 'size-8 rounded-md p-1.5',
        'icon-lg': 'size-10 rounded-lg p-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    onClickAsync,
    onClick,
    disabled,
    children,
    isLoading: isLoadingProp,
    loadingText,
    ...rest
  } = props;
  const [isLoading, handleClick] = useLoadingCallback(onClick, onClickAsync);

  const resolvedIsLoading = isLoading || isLoadingProp;
  const hasLoadingText = loadingText !== undefined && loadingText !== null && loadingText !== '';
  const loadingTextIsMuchShorter = isLoadingTextMuchShorter(children, loadingText);

  // Slot merges the button props onto its one child. The spinner spans would become
  // that child, and the link would sit inside them with part of the click area and the
  // focus ring outside it. A link does not need the spinner.
  if (props.asChild) {
    return (
      <ShadcnButton
        {...rest}
        ref={ref}
        onClick={handleClick}
        disabled={disabled}
        className={cn(
          solinkButtonVariants({ variant: props.variant, size: props.size }),
          props.className,
        )}
      >
        {children}
      </ShadcnButton>
    );
  }

  return (
    <ShadcnButton
      {...rest}
      ref={ref}
      onClick={handleClick}
      disabled={resolvedIsLoading || disabled}
      className={cn(
        solinkButtonVariants({ variant: props.variant, size: props.size }),
        props.className,
      )}
    >
      <span className="relative inline-flex items-center justify-center gap-2">
        <span
          className={cn('inline-flex items-center gap-2', { invisible: resolvedIsLoading })}
          aria-hidden={resolvedIsLoading || undefined}
        >
          {children}
        </span>
        {resolvedIsLoading && (
          <span
            className={cn(
              'absolute inset-0 flex items-center gap-2 overflow-hidden',
              hasLoadingText && !loadingTextIsMuchShorter ? 'justify-evenly' : 'justify-center',
            )}
          >
            <Spinner className="shrink-0" />
            {hasLoadingText && <span className="min-w-0 truncate">{loadingText}</span>}
          </span>
        )}
      </span>
    </ShadcnButton>
  );
});
Button.displayName = 'Button';
